<script setup lang="ts">
import { Expenses } from '../types/expenses'
import { onBeforeMount, ref, watch } from 'vue'
import { useExpenses } from '../store/useExpenses'
import { useI18n } from 'vue-i18n'
const props = withDefaults(
  defineProps<{
    expenses: Expenses
    date?: Date
  }>(),
  {
    date: () => new Date(),
  }
)

//translations
const { t } = useI18n({ useScope: 'global' })
const formLabel = (key: string) => t(`form.${key}`)

const expensesEdits = ref<(typeof props)['expenses']>([])
onBeforeMount(() => {
  props.expenses.forEach((expense) => expensesEdits.value.push({ ...expense }))
  expensesEdits.value.push({
    id: 0,
    description: null,
    amount: null,
    currency: 'string',
    date: props.date,
  })
})
watch(props, () => {
  expensesEdits.value = []
  props.expenses.forEach((expense) => expensesEdits.value.push({ ...expense }))
  expensesEdits.value.push({
    id: 0,
    description: null,
    amount: null,
    currency: 'string',
    date: props.date,
  })
})

const removeExpense = (index: number) => {
  expensesEdits.value.splice(index, 1)
}
const add = () => {
  expensesEdits.value.push({
    id: expensesEdits.value.length,
    description: null,
    amount: null,
    currency: 'string',
    date: props.date,
  })
}
const save = () => {
  const { saveExpensesByDate } = useExpenses()
  const toSave = expensesEdits.value.filter((expense) => {
    return expense.amount !== null && expense.description !== null
  })
  saveExpensesByDate(
    toSave.map((expense) => {
      return { ...expense, amount: Number(expense.amount) }
    }),
    props.date
  )
}

/**check if key is a number */
const isALetter = (text: string): boolean => {
  //things like backspace etc allowed
  if (text.length > 1) return false
  const numb = new RegExp(/[^a-zA-Z]/)
  return !numb.test(String(text))
}
/**Logic to prevent input of non digits */
const captureNumber = (e: KeyboardEvent) => {
  if (isALetter(e.key)) {
    e.preventDefault()
  }
}
</script>
<template>
  <q-card-section class="q-pt-none q-col-gutter-y-md">
    <div
      v-for="(expense, expenseIndex) in expensesEdits"
      :key="expense.id"
      class="row items-center q-col-gutter-x-sm"
    >
      <div class="col">
        <q-input
          filled
          color="accent"
          v-model="expensesEdits[expenseIndex].description"
          :label="formLabel('description')"
        />
      </div>
      <div class="col-3">
        <q-input
          filled
          color="accent"
          v-model="expensesEdits[expenseIndex].amount"
          :label="formLabel('amount')"
          type="number"
          prefix="$"
          @keydown="captureNumber"
        />
      </div>
      <div class="col-1">
        <q-btn
          v-if="expenseIndex + 1 < expensesEdits.length"
          @click="removeExpense(expenseIndex)"
          round
          color="accent"
          icon="remove"
          size="sm"
        />
        <q-btn v-else @click="add" round color="accent" icon="add" size="sm" />
      </div>
    </div>
  </q-card-section>
  <slot :expenses-edits="expensesEdits" name="actions">
    <q-card-actions align="right" class="text-primary">
      <q-btn rounded flat :label="formLabel('close')" v-close-popup />
      <q-btn rounded color="primary" @click="save" v-close-popup>
        <span class="q-px-sm">{{ formLabel('save') }}</span>
      </q-btn>
    </q-card-actions>
  </slot>
</template>
